import NetInfo, { NetInfoState } from '@react-native-community/netinfo'
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { AppState, Platform } from 'react-native'

NetInfo.configure({
  reachabilityLongTimeout: 60 * 1000,
  reachabilityShortTimeout: 15 * 1000,
  reachabilityRequestTimeout: 30 * 1000,
})

interface INetworkContextData {
  isConnected: boolean
  isInternetReachable: boolean | null
  handleReload: () => void
}

export const NetworkContext = createContext<INetworkContextData>(
  {} as INetworkContextData,
)

function getConnectionStatus(state: NetInfoState): boolean {
  // Reachability indeterminada (null) não é offline: no primeiro estado emitido
  // o ping de reachability ainda não resolveu e colapsar null em false pisca a
  // tela de "sem conexão" durante o load.
  return state.isConnected !== false && state.isInternetReachable !== false
}

export const NetworkContextProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(true)
  const [isInternetReachable, setIsInternetReachable] = useState<
    boolean | null
  >(true)

  const handleReload = useCallback(() => {
    NetInfo.refresh().then((state) => {
      setIsConnected(getConnectionStatus(state))
      setIsInternetReachable(state.isInternetReachable)
    })
  }, [])

  useEffect(() => {
    NetInfo.fetch().then((state: NetInfoState) => {
      setIsConnected(getConnectionStatus(state))
      setIsInternetReachable(state.isInternetReachable)
    })

    const unsubscribe = NetInfo.addEventListener((state: NetInfoState) => {
      setIsConnected(getConnectionStatus(state))
      setIsInternetReachable(state.isInternetReachable)

      if (state.isConnected && state.isInternetReachable === null) {
        NetInfo.refresh()
      }
    })

    return () => {
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (Platform.OS !== 'ios') return

    const subscription = AppState.addEventListener('change', (nextState) => {
      if (nextState === 'active') {
        NetInfo.refresh()
      }
    })

    return () => {
      subscription.remove()
    }
  }, [])

  const contextValue = useMemo(
    () => ({ isConnected, isInternetReachable, handleReload }),
    [isConnected, isInternetReachable, handleReload],
  )

  return (
    <NetworkContext.Provider value={contextValue}>
      {children}
    </NetworkContext.Provider>
  )
}
